import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useI18n, localizeAssetTypeLabel } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { useActiveAssetListBook } from '../hooks/useActiveAssetListBook';
import { useBookableAssetSearch } from '../hooks/useBookableAssetSearch';
import { isAssetKeywordQueryActive } from '../utils/assetKeywordSearch';
import { assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import { rememberBrowseAssetListSnapshot } from '../utils/browseAssetListSnapshot';
import { israelSettlementDisplayLabel } from '../utils/israelSettlements';
import { ASSET_SEARCH_KEYWORD_DEBOUNCE_MS, hasClientSideBrowseFilters } from '../api/assetSearchClient';
import { isVoiceAssistantEnabled } from '../assistant/isVoiceAssistantEnabled';
import { VoiceSearchControls } from '../assistant/VoiceSearchControls';
import { useAssistant } from '../assistant/AssistantProvider';
import { catalogBrowseParamsForSearchIntent } from '../assistant/executeAssistantIntent';
import { parseAssistantIntent, type AssistantIntent } from '../assistant/parseAssistantIntent';
import {
  commitAssistantSearchHits,
  isAssistantSearchHitsSession,
} from '../assistant/assistantSearchHits';
import { assistantSearchChipFromIntent } from '../assistant/formatAssistantSearchChip';
import { BookableAssetSearchResults } from './BookableAssetSearchResults';
import { AssetSearchListFilters, useAssetSearchListFilters } from './AssetSearchListFilters';
import styles from './AssetSearchView.module.css';
import type { MessageKey } from '../i18n/messages';
import type { AppLanguage } from '../i18n/types';
import type { TranslateParams } from '../i18n/translate';

type AssetSearchViewProps = {
  returnView?: 'search' | null;
  onSelectAsset?: () => void;
};

function voiceSearchStatus(
  intent: AssistantIntent,
  language: AppLanguage,
  t: (key: MessageKey, params?: TranslateParams) => string,
): string | null {
  if (intent.kind !== 'search') {
    return t('assistant.result.search', { q: intent.raw });
  }
  const typeLabel = intent.assetTypeLabel
    ? localizeAssetTypeLabel(intent.assetTypeLabel, language, t)
    : null;
  const cityLabel = intent.city ? israelSettlementDisplayLabel(intent.city, language) : null;
  const chip = assistantSearchChipFromIntent(intent, language);
  if (chip) {
    return intent.assetTypeLabel && typeLabel
      ? t('assistant.result.searchTypeSlot', { type: typeLabel, chip })
      : t('assistant.result.searchSlot', { chip });
  }
  if (typeLabel && cityLabel) {
    return t('assistant.result.searchTypeCity', { type: typeLabel, city: cityLabel });
  }
  if (typeLabel) {
    return t('assistant.result.searchType', { type: typeLabel });
  }
  if (cityLabel) {
    return t('assistant.result.searchCity', { city: cityLabel });
  }
  return t('assistant.result.search', { q: intent.keyword || intent.raw });
}

export function AssetSearchView({ returnView = 'search', onSelectAsset }: AssetSearchViewProps = {}) {
  const { t, language } = useI18n();
  const { showAssetInBrowse, searchQuery, updateSearchQuery, openAssets } = useAppNavigation();
  const assistant = useAssistant();
  const inputId = useId();
  const [keyword, setKeyword] = useState(() => searchQuery ?? '');
  const [voiceStatus, setVoiceStatus] = useState<string | null>(null);
  const voiceEnabled = isVoiceAssistantEnabled();
  const listFilters = useAssetSearchListFilters(keyword);
  const filtersActive = hasClientSideBrowseFilters(listFilters.uiState);

  useEffect(() => {
    const fromNav = searchQuery ?? '';
    setKeyword((prev) => (prev === fromNav ? prev : fromNav));
  }, [searchQuery]);

  useEffect(() => {
    const trimmed = keyword.trim();
    const current = searchQuery?.trim() || null;
    if (trimmed === (current ?? '')) {
      return;
    }
    const timer = window.setTimeout(() => {
      updateSearchQuery(trimmed || null);
    }, ASSET_SEARCH_KEYWORD_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keyword, searchQuery, updateSearchQuery]);

  const { showBook, onBook, bookDisabled, bookDisabledReason } = useActiveAssetListBook();

  const onBookWithMemory = useCallback(
    (asset: Record<string, unknown>) => {
      const id = assetRecordPrimaryId(asset);
      if (id) {
        assistant?.rememberAssetForBooking(id, asset);
      }
      onBook(asset);
    },
    [assistant, onBook],
  );

  const trimmedKeyword = keyword.trim();
  const keywordActive = isAssetKeywordQueryActive(trimmedKeyword);
  const searchEnabled = keywordActive || filtersActive;
  const search = useBookableAssetSearch(listFilters.uiState, {
    enabled: searchEnabled,
    debounceMs: ASSET_SEARCH_KEYWORD_DEBOUNCE_MS,
    language,
  });
  const displayedRecords = useMemo(
    () => listFilters.applySort(search.records, keywordActive),
    [keywordActive, listFilters, search.records],
  );

  useEffect(() => {
    if (!isAssistantSearchHitsSession()) {
      return;
    }
    if (!searchEnabled || !search.settled) {
      return;
    }
    commitAssistantSearchHits(displayedRecords, { loaded: true });
  }, [displayedRecords, search.settled, searchEnabled]);

  const onSelect = useCallback(
    (asset: Record<string, unknown>) => {
      const id = assetRecordPrimaryId(asset);
      if (!id) {
        return;
      }
      rememberBrowseAssetListSnapshot(asset);
      assistant?.rememberAssetForBooking(id, asset);
      onSelectAsset?.();
      if (trimmedKeyword) {
        updateSearchQuery(trimmedKeyword);
      }
      showAssetInBrowse(id, { assetTypeId: null, returnView });
    },
    [assistant, onSelectAsset, returnView, showAssetInBrowse, trimmedKeyword, updateSearchQuery],
  );

  const onVoiceTranscript = useCallback((text: string) => {
    setKeyword(text);
    setVoiceStatus(null);
  }, []);

  const onVoiceFinal = useCallback(
    (text: string) => {
      const intent = parseAssistantIntent(text);
      const browse = catalogBrowseParamsForSearchIntent(intent, 'search');
      if (browse) {
        openAssets(browse);
        setVoiceStatus(voiceSearchStatus(intent, language, t));
        return;
      }
      const next =
        intent.kind === 'search' ? intent.keyword.trim() || text.trim() : text.trim();
      setKeyword(next);
      updateSearchQuery(next || null);
      setVoiceStatus(voiceSearchStatus(intent, language, t));
    },
    [language, openAssets, t, updateSearchQuery],
  );

  const hasResults = displayedRecords.length > 0;
  const initialLoading = search.loading && !hasResults;

  let statusMessage: string | null = null;
  if (!searchEnabled && !trimmedKeyword) {
    statusMessage = t('header.searchPrompt');
  } else if (!searchEnabled && trimmedKeyword && !keywordActive) {
    statusMessage = t('header.searchMinLength');
  } else if (voiceStatus && (initialLoading || !keywordActive)) {
    statusMessage = voiceStatus;
  } else if (search.error) {
    statusMessage = search.error;
  } else if (initialLoading) {
    statusMessage = voiceStatus || t('header.searchLoading');
  }

  return (
    <div className={styles.root}>
      <div className={styles.searchField}>
        <label className={styles.searchLabel} htmlFor={inputId}>
          {t('header.searchLabel')}
        </label>
        <div className={styles.searchInputRow}>
          <input
            id={inputId}
            type="search"
            className={styles.searchInput}
            value={keyword}
            onChange={(event) => {
              setVoiceStatus(null);
              setKeyword(event.target.value);
            }}
            placeholder={t('header.searchPlaceholder')}
            autoComplete="off"
            enterKeyHint="search"
            autoFocus
          />
          {voiceEnabled ? (
            <VoiceSearchControls
              variant="inline"
              onTranscript={onVoiceTranscript}
              onFinalUtterance={onVoiceFinal}
            />
          ) : null}
        </div>
        {voiceEnabled ? <p className={styles.voiceHint}>{t('assistant.voice.hint')}</p> : null}
      </div>

      <AssetSearchListFilters
        filters={listFilters}
        resultCount={
          searchEnabled && (hasResults || search.catalogExpanding)
            ? {
                count: displayedRecords.length,
                total: search.total,
                expanding: search.catalogExpanding,
              }
            : null
        }
      />

      {statusMessage ? (
        <p
          className={search.error ? styles.statusError : styles.status}
          role={search.error ? 'alert' : 'status'}
        >
          {statusMessage}
        </p>
      ) : null}

      {searchEnabled && !search.error && (hasResults || !initialLoading) ? (
        <div className={styles.results}>
          <BookableAssetSearchResults
            records={displayedRecords}
            total={search.total}
            loading={search.loading}
            loadingMore={search.loadingMore}
            catalogExpanding={search.catalogExpanding}
            hasMore={search.hasMore}
            searchMode={search.searchMode}
            onSelect={onSelect}
            onLoadMore={search.loadMore}
            showBook={showBook}
            onBook={onBookWithMemory}
            bookDisabled={bookDisabled}
            bookDisabledReason={bookDisabledReason}
            theme="light"
            emptyMessage={t('header.searchNoResults')}
            hideCount
          />
        </div>
      ) : null}
    </div>
  );
}
